import React, { useMemo, useState, useEffect } from 'react';
import { GoogleMap, MarkerF, InfoWindowF, CircleF } from '@react-google-maps/api';
import { Paper, Typography, Box, Chip, Rating, CircularProgress } from '@mui/material';
import { LocationOn as LocationIcon, Warning as WarningIcon } from '@mui/icons-material';

const BusinessMap = ({ 
  businesses, 
  userLocation,
  searchCenter, // New prop for search center coordinates
  containerStyle = { width: '100%', height: '400px' }
}) => {
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  
  useEffect(() => {
    // Check if Google Maps is available
    if (window.google && window.google.maps) {
      setMapLoaded(true);
    } else {
      console.error('Google Maps not found. Make sure it is loaded in index.html');
    }
  }, []);

  // Filter businesses with valid coordinates
  const validBusinesses = useMemo(() => {
    return businesses.filter(business => {
      const coordinates = business.geocodeResults?.location?.coordinates;
      return coordinates && 
             Array.isArray(coordinates) && 
             coordinates.length === 2 &&
             coordinates[0] != null && 
             coordinates[1] != null;
    });
  }, [businesses]);

  // Determine if search center coordinates are valid
  const hasValidSearchCenter = useMemo(() => {
    return searchCenter && 
           Array.isArray(searchCenter) && 
           searchCenter.length === 2 && 
           searchCenter[0] != null && 
           searchCenter[1] != null;
  }, [searchCenter]);

  // Convert search center to Google Maps format if valid
  const formattedSearchCenter = useMemo(() => {
    if (!hasValidSearchCenter) return null;
    return {
      lat: searchCenter[1], // latitude is the second element
      lng: searchCenter[0]  // longitude is the first element
    };
  }, [hasValidSearchCenter, searchCenter]);

  // Calculate map center and bounds
  const mapConfig = useMemo(() => {
    if (!window.google) {
      return {
        center: userLocation 
          ? { lat: userLocation.lat, lng: userLocation.lng }
          : { lat: 40.7128, lng: -74.0060 }, // New York as fallback
        zoom: 12
      };
    }

    // Calculate bounds to fit all markers
    const bounds = new window.google.maps.LatLngBounds();
    
    // Add valid businesses to bounds
    if (validBusinesses.length > 0) {
      validBusinesses.forEach(business => {
        const [lng, lat] = business.geocodeResults.location.coordinates;
        bounds.extend({ lat, lng });
      });
    }

    // Add search center to bounds if available
    if (formattedSearchCenter) {
      bounds.extend(formattedSearchCenter);
    }

    // Add user location to bounds if available
    if (userLocation) {
      bounds.extend(userLocation);
    }

    // If no points were added to bounds (very unlikely with search center)
    if (bounds.isEmpty()) {
      return {
        center: formattedSearchCenter || userLocation || { lat: 40.7128, lng: -74.0060 }, // Priority: search center > user location > NY
        zoom: 12
      };
    }

    return {
      center: {
        lat: bounds.getCenter().lat(),
        lng: bounds.getCenter().lng()
      },
      zoom: 12, // Default zoom, will be adjusted by fitBounds
      bounds: bounds
    };
  }, [validBusinesses, userLocation, formattedSearchCenter]);

  // Function to handle map load and possibly fit bounds
  const handleMapLoad = (map) => {
    if (map && mapConfig.bounds && !mapConfig.bounds.isEmpty()) {
      map.fitBounds(mapConfig.bounds);
    }
  };

  if (!mapLoaded) {
    return (
      <Paper 
        sx={{ 
          p: 3, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}
      >
        <CircularProgress size={24} sx={{ mr: 2 }} />
        <Typography>Loading map...</Typography>
      </Paper>
    );
  }

  // Show message if no businesses have valid coordinates
  if (validBusinesses.length === 0 && !formattedSearchCenter && !userLocation) {
    return (
      <Paper 
        sx={{ 
          p: 3, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: 'warning.light'
        }}
      >
        <WarningIcon sx={{ mr: 1 }} />
        <Typography>
          {businesses.length === 0 
            ? 'No businesses to display on map'
            : 'None of the current businesses have valid location data'}
        </Typography>
      </Paper>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={mapConfig.center}
      zoom={mapConfig.zoom}
      onLoad={handleMapLoad}
      options={{
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true
      }}
    >
      {/* Search center marker and circle */}
      {formattedSearchCenter && (
        <>
          <MarkerF
            position={formattedSearchCenter}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: "#FF5722", // Orange color for search center
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 2,
            }}
            title="Search Center"
          />
          <CircleF
            center={formattedSearchCenter}
            radius={8047} // 5 miles in meters (default display radius)
            options={{
              strokeColor: '#FF5722',
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillColor: '#FF5722',
              fillOpacity: 0.1,
            }}
          />
        </>
      )}

      {/* User location marker */}
      {userLocation && (
        <MarkerF
          position={userLocation}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: '#4285F4', // Blue color for user location
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          }}
          title="Your Location"
        />
      )}

      {/* Business markers */}
      {validBusinesses.map(business => {
        const [lng, lat] = business.geocodeResults.location.coordinates;
        return (
          <MarkerF
            key={business._id || business.businessId}
            position={{ lat, lng }}
            onClick={() => setSelectedBusiness(business)}
          />
        );
      })}

      {/* Info Window */}
      {selectedBusiness && (
        <InfoWindowF
          position={{
            lat: selectedBusiness.geocodeResults.location.coordinates[1],
            lng: selectedBusiness.geocodeResults.location.coordinates[0]
          }}
          onCloseClick={() => setSelectedBusiness(null)}
        >
          <Box sx={{ maxWidth: 200 }}>
            <Typography variant="subtitle1" gutterBottom>
              {selectedBusiness.businessName}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {selectedBusiness.skillName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Rating
                value={selectedBusiness.avgSkillRating_all}
                precision={0.1}
                readOnly
                size="small"
              />
              <Typography variant="body2" sx={{ ml: 1 }}>
                ({selectedBusiness.count_all})
              </Typography>
            </Box>
            
            {/* Display distance from search center if available */}
            {formattedSearchCenter && selectedBusiness.distanceFromSearchCenter && (
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <LocationIcon fontSize="small" sx={{ mr: 0.5, color: 'warning.main' }} />
                {selectedBusiness.distanceFromSearchCenter.toFixed(1)} miles from search
              </Typography>
            )}
            
            {/* Display distance from user if available */}
            {userLocation && (
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <LocationIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
                {calculateDistance(
                  userLocation.lat,
                  userLocation.lng,
                  selectedBusiness.geocodeResults.location.coordinates[1],
                  selectedBusiness.geocodeResults.location.coordinates[0]
                ).toFixed(1)} miles from you
              </Typography>
            )}
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {selectedBusiness.featureMap?.slice(0, 2).map((feature) => (
                <Chip
                  key={feature}
                  label={feature}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        </InfoWindowF>
      )}
    </GoogleMap>
  );
};

// Helper function to calculate distance
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  if (!lat1 || !lng1 || !lat2 || !lng2) return 0;
  
  // Haversine formula implementation
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

export default BusinessMap;